import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Calendar, Clock, User, ArrowRight, Share2, Linkedin, Twitter } from 'lucide-react';
import Navigation from '../../components/Navigation';
import Footer from '../../../Landing/footer';
import { BlogPost } from '../../../../data/blogPosts';

const Page = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const MainContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px 24px 60px;
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 60px;
  position: relative;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    max-width: 800px;
  }
`;

const ArticleContent = styled.article`
  min-width: 0; // Prevent overflow in grid
`;

const Sidebar = styled.aside`
  @media (max-width: 1024px) {
    display: none;
  }
`;

const StickyContainer = styled.div`
  position: sticky;
  top: 120px;
`;

const TOC = styled.nav`
  margin-bottom: 40px;

  h4 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    margin-bottom: 16px;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    border-left: 2px solid #e5e7eb;
  }

  li {
    margin: 0;
  }

  a {
    display: block;
    padding: 8px 0 8px 16px;
    font-size: 14px;
    color: #6b7280;
    text-decoration: none;
    transition: all 0.2s ease;
    border-left: 2px solid transparent;
    margin-left: -2px;

    &:hover {
      color: #111827;
      border-left-color: #d1d5db;
    }

    &.active {
      color: #7c3aed;
      border-left-color: #7c3aed;
      font-weight: 500;
    }
  }
`;

const ShareWidget = styled.div`
  display: flex;
  gap: 12px;
  
  button {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid #e5e7eb;
    background: white;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #7c3aed;
      color: #7c3aed;
      transform: translateY(-2px);
    }
  }
`;

// --- Hero Section Styles ---

const HeroSection = styled.header`
  margin-bottom: 60px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;
  margin-bottom: 40px;
  transition: color 0.2s ease;

  &:hover {
    color: #111827;
  }
`;

const Category = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #4b5563; /* Neutral gray */
  background: #f3f4f6; /* Light gray bg */
  padding: 6px 12px;
  border-radius: 100px;
  display: inline-block;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 48px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  color: #6b7280;
  font-size: 15px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 40px;
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// --- CTA Section ---

const CTASection = styled.div`
  margin-top: 80px;
  padding: 60px 40px;
  background: linear-gradient(135deg, #111827 0%, #1f2937 100%); /* Dark gradient */
  border-radius: 24px;
  border: 1px solid #374151;
  position: relative;
  overflow: hidden;
  text-align: center;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  }
`;

const CTATitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #ffffff; /* White text */
  margin-bottom: 16px;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const CTADescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: #9ca3af; /* Light gray text */
  margin-bottom: 32px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff; /* White button */
  color: #111827; /* Dark text */
  padding: 16px 32px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 16px;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);

  &:hover {
    background: #f3f4f6;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`;

interface TOCItem {
  id: string;
  label: string;
  level?: number; // 1 or 2 (for indentation)
}

interface BlogPostLayoutProps {
  post: BlogPost;
  children: React.ReactNode;
  toc?: TOCItem[];
}

const BlogPostLayout: React.FC<BlogPostLayoutProps> = ({ post, children, toc }) => {
  const [activeId, setActiveId] = useState<string>('');
  const pageUrl = `https://smushlabs.ai/blog/${post.slug}`;
  const imageUrl = post.imageUrl
    ? new URL(post.imageUrl, 'https://smushlabs.ai').href
    : 'https://smushlabs.ai/social-preview.png';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    url: pageUrl,
    mainEntityOfPage: pageUrl,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Person',
      name: post.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'SmushLabs',
      url: 'https://smushlabs.ai',
      logo: {
        '@type': 'ImageObject',
        url: 'https://smushlabs.ai/favicon.ico',
      },
    },
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
      contentUrl: imageUrl,
      caption: post.imageAlt || post.title,
    },
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -60% 0px' }
    );

    if (toc) {
      toc.forEach(({ id }) => {
        const element = document.getElementById(id);
        if (element) observer.observe(element);
      });
    }

    return () => observer.disconnect();
  }, [toc]);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Header height
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Page>
      <Helmet>
        <title>{post.title} | SmushChat Blog</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:alt" content={post.imageAlt || post.title} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Navigation />

      <MainContainer>
        <ArticleContent>
          <BackLink to="/blog">
            <ArrowLeft size={20} />
            Back to Blog
          </BackLink>

          <HeroSection>
            <Category>{post.category}</Category>
            <Title>{post.title}</Title>
            <Meta>
              <MetaItem>
                <User size={18} />
                {post.author}
              </MetaItem>
              <MetaItem>
                <Calendar size={18} />
                {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </MetaItem>
              <MetaItem>
                <Clock size={18} />
                {post.readTime}
              </MetaItem>
            </Meta>
          </HeroSection>

          {children}

          {post.ctaDescription && (
            <CTASection>
              <CTATitle>Turn Visitors into Customers on Autopilot</CTATitle>
              <CTADescription>{post.ctaDescription}</CTADescription>
              <CTAButton to="/">
                Build Your AI Agent
                <ArrowRight size={20} />
              </CTAButton>
            </CTASection>
          )}
        </ArticleContent>

        <Sidebar>
          <StickyContainer>
            {toc && toc.length > 0 && (
              <TOC>
                <h4>Table of Contents</h4>
                <ul>
                  {toc.map(({ id, label, level }) => (
                    <li key={id} style={{ paddingLeft: level === 2 ? '16px' : '0' }}>
                      <a
                        href={`#${id}`}
                        className={activeId === id ? 'active' : ''}
                        onClick={(e) => scrollToSection(e, id)}
                      >
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </TOC>
            )}

            <TOC>
              <h4>Share this post</h4>
              <ShareWidget>
                <button aria-label="Share on Twitter"><Twitter size={18} /></button>
                <button aria-label="Share on LinkedIn"><Linkedin size={18} /></button>
                <button aria-label="Copy Link"><Share2 size={18} /></button>
              </ShareWidget>
            </TOC>
          </StickyContainer>
        </Sidebar>
      </MainContainer>

      <Footer />
    </Page>
  );
};

export default BlogPostLayout;
